/**
 * canvasOpening — **une seule règle pour les cinq chemins d'ouverture** (#860).
 *
 * ## Ce que ce module remplace
 *
 * Cinq chemins ouvrent le canvas sans que l'avocat l'ait demandé : la demande de
 * mode rédaction de l'agent, le premier bloc réellement streamé d'un acte, le
 * chargement d'un `.docx`, le premier lot d'actions de la cascade, et la surface
 * agent canonique. Chacun s'était écrit sa propre version du même geste —
 * bascule de l'interrupteur, attente du montage, réponse en cas d'échec — et
 * aucune n'était d'accord avec les autres.
 *
 * Elles partageaient surtout le même trou, hérité de l'architecture d'avant :
 * le canvas n'existait que dans l'écran assistant, si bien qu'une demande reçue
 * ailleurs basculait un interrupteur que personne ne lisait. « Mettre en
 * attente » était le comportement, sans que personne l'ait décidé (#827).
 *
 * ## Ce que #856 a changé, et que ce module encaisse
 *
 * Le canvas habite maintenant la coque : son nœud hôte vit dans le corps du
 * document et le panneau s'y monte **quelle que soit la page affichée**. Il n'y
 * a donc plus rien à refuser, plus rien à différer, plus de file d'attente : une
 * demande d'ouverture aboutit toujours, et le document est simplement invisible
 * tant que l'avocat n'est pas revenu dans l'assistant.
 *
 * ## Les deux gestes, et pourquoi ils sont séparés
 *
 * `openCanvas` **ouvre** — c'est tout, et c'est instantané. Les chemins qui
 * veulent seulement montrer quelque chose à l'avocat (demande de mode rédaction,
 * premier bloc streamé) s'arrêtent là : ils n'ont rien à attendre.
 *
 * `waitForCanvasEditorReady` attend que l'éditeur existe, et seuls les chemins
 * qui vont **écrire dedans** l'appellent. Cette attente-là est irréductible : on
 * ne peut pas appliquer un lot d'actions à un éditeur qui n'est pas construit,
 * et répondre « réessaie » ferait boucler l'agent — le refus muet que #827
 * écarte explicitement.
 *
 * ## Ce qui disparaît, et ce qui reste (corrigé en revue)
 *
 * Ce qui disparaît est le **sondage** : deux copies d'une boucle qui
 * interrogeait le store tous les 100 ms, et qui ne s'armait que `if (wasOff)` —
 * c'est-à-dire seulement dans le cas qu'elle venait elle-même de provoquer. Un
 * canvas déjà ouvert mais pas encore prêt (échange de gabarit en cours, panneau
 * encore en vol, second lot arrivé pendant le montage du premier) sautait
 * l'attente et échouait sur-le-champ. **La garde portait sur « ai-je allumé ? »
 * au lieu de « est-ce prêt ? » — c'était ça, le défaut.**
 *
 * Ce qui **reste**, et qu'une première version de ce module avait retiré à
 * tort : la **borne**. Elle ne servait pas à protéger d'un montage impossible ;
 * elle garantissait deux choses qu'une attente sans fin détruit.
 *  1. *Le front répond toujours.* Un montage qui n'aboutit jamais — gabarit
 *     vierge introuvable, crash SuperDoc attrapé par la frontière d'erreur —
 *     laisse la session en `loading` pour toujours : sans borne, aucune trame de
 *     résultat n'est émise et l'agent n'apprend rien de ce qui s'est passé.
 *  2. *Un lot ne s'applique jamais après l'abandon de son demandeur.* Le
 *     backend borne toute attente de résultat canvas (`waitForCanvasOpResult`,
 *     `SessionState.ts`) ; passé ce délai il rend un échec et l'agent rejoue.
 *     Une attente sans borne appliquerait quand même le premier lot en se
 *     réveillant, et l'acte de l'avocat contiendrait le plan **deux fois**.
 * D'où `CANVAS_MOUNT_WAIT_MS`, calé **sous** le plus court des délais backend.
 */

import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import { estUnFilConfirme } from './canvasConversationGate';
import { estEnCoursDeSuppression } from './filsEnSuppression';
import { lireCanvasDeploye, useCanvasVisibiliteStore } from './canvasVisibilite';
import { isCanvasSessionProtectedByWork } from './canvasSessionProtection';
import type { SuperDocInstance } from '@superdoc/react';

/**
 * Combien de temps on attend qu'un canvas devienne écrivable.
 *
 * **Doit rester strictement inférieur au plus court délai du demandeur
 * backend** — aujourd'hui 15 s : le défaut de `waitForCanvasOpResult`
 * (`SessionState.ts`). Viennent ensuite 20 s pour une écriture ciblée
 * (`MUTATION_TIMEOUT_MS`, `SuperDocCanvasTools.ts`), 30 s pour `open_canvas`
 * (`CANVAS_OPEN_ACK_TIMEOUT_MS`, sous une échéance unique du navigateur) et 45 s
 * pour une lecture (`INSPECT_TIMEOUT_MS`, relevée de 15 s le 2026-10-12). Ces symboles sont GREPPABLES : les relire
 * plutôt que se fier à ce commentaire.
 *
 * ⚠️ La borne de lecture backend est dimensionnée sur CETTE valeur : avant
 * d'exécuter une lecture, le navigateur peut enchaîner
 * `CANVAS_DRAFT_LOOKUP_TIMEOUT_MS` (8 s), `DEFAULT_HYDRATION_TIMEOUT_MS` (14 s)
 * et cette attente (14 s), soit 36 s sans échéance commune, sous les 45 s.
 * Relever l'une des trois sans relire `INSPECT_TIMEOUT_MS` refait expirer des
 * lectures qui aboutissent (29 sur 37 délais le 2026-10-12, à 15 s).
 * (Historique : la borne a longtemps été justifiée par un délai de 12 s de
 * `CanvasTools.requestActions` — module supprimé par #935, et ce 12 s
 * n'existe plus nulle part. La marge n'a fait que s'élargir.)
 * C'est cette marge qui garantit que le front répond
 * avant que le backend renonce : au-delà, le demandeur a déjà rendu un échec au
 * modèle, qui a pu rejouer sa demande, et appliquer le lot d'origine
 * dupliquerait le travail dans le document de l'avocat.
 *
 * 14 s couvre un premier montage lent observé (~10 s, chargement différé de
 * ~1,7 Mo et construction DOCX compris) tout en restant sous ce plus court délai.
 */
export const CANVAS_MOUNT_WAIT_MS = 14_000;

/** Ce que l'ouverture a réellement changé — sert aux traces, jamais à décider. */
export interface CanvasOpenOutcome {
  /** Le canvas était déjà déployé : la demande n'a rien changé dans le canvas. */
  alreadyOpen: boolean;
}

/**
 * Ouvrir le canvas. Aboutit toujours, où que soit l'avocat.
 *
 * Déployer pose aussi `aDejaEteDeploye`, qui est ce qui fait monter le panneau
 * dans la coque — c'est par là que le document s'ouvre et se remplit même quand
 * l'écran affiche un dossier.
 *
 * ⚠️ Déployer ne rend pas forcément le canvas **visible** : depuis le
 * 2026-08-13 il ne s'affiche que sur l'écran de la conversation qui détient le
 * document (`canvasVisibilite`). Les chemins agent n'ont pas à s'en soucier — ils
 * prennent le canvas avant d'écrire, donc le rendent visible par là où il compte.
 *
 * @param reason - motif lisible, pour la trace du chemin appelant.
 */
export function openCanvas(reason: string): CanvasOpenOutcome {
  const alreadyOpen = lireCanvasDeploye();
  // Même déjà déployé, un nouveau geste doit publier une séquence : après un
  // checkpoint réseau refusé, le bouton reste justement « Ouvrir » dans la
  // conversation cible et ce second clic constitue son retry explicite.
  useCanvasVisibiliteStore.getState().setDeploye(true);
  if (!alreadyOpen) {
    console.log(`[canvas] déploiement — ${reason}`);
  }
  return { alreadyOpen };
}

/**
 * Rendre son canvas à l'avocat qui revient dans un fil où Hector travaille
 * encore sur le document (conversation de production du 2026-10-12).
 *
 * Le canvas vide d'un plan en préparation est masqué quand l'avocat part sur
 * « Nouvelle conversation » (`AgentV3View`), et l'ouverture par job ne joue
 * qu'une fois par job (`WebSocketPool.openCanvasForRedactionJob`) : sans ce
 * retour, il retrouvait une carte de plan qui avance et un canvas fermé.
 *
 * Trois conditions, toutes nécessaires :
 * - **le canvas de ce fil a été masqué par la navigation**, pas par la croix :
 *   fermer le canvas est une décision de l'avocat qu'aucun retour ne défait ;
 * - **Hector y travaille** : épingle de tour documentaire, d'admission de job
 *   ou de flux, écriture ou mutation en cours (`isCanvasSessionProtectedByWork`).
 *   Un tour de chat qui ne touche pas au document n'épingle rien ;
 * - **le canvas est fermé** : `openCanvas` compte comme un nouveau geste
 *   « Ouvrir », qui efface un échec de chargement affiché (`setDeploye`).
 *
 * Le souvenir du masquage est consommé au retour, que le canvas rouvre ou non.
 */
export function rouvrirLeCanvasAuRetourDansLeFil(conversationId: string): void {
  const { masquesEnQuittantLeFil } = useCanvasVisibiliteStore.getState();
  if (!masquesEnQuittantLeFil[conversationId]) return;
  const restants = { ...masquesEnQuittantLeFil };
  delete restants[conversationId];
  useCanvasVisibiliteStore.setState({ masquesEnQuittantLeFil: restants });
  if (!isCanvasSessionProtectedByWork(conversationId) || lireCanvasDeploye()) return;
  openCanvas(`retour dans le fil ${conversationId}, où Hector travaille sur le document`);
}

/**
 * Ouvrir le canvas **pour l'avocat**, dans la conversation qu'il regarde.
 *
 * Le pendant humain d'`openCanvasForAgentWrite` (`WebSocketPool`), et il a fallu
 * l'écrire le 2026-08-13 : jusque-là le geste de l'avocat n'avait pas à prendre
 * le canvas, parce que **la navigation le cédait toute seule** — afficher une
 * conversation lui donnait le document. C'est précisément ce couplage qu'on
 * supprime (`canvasVisibilite`), donc la prise doit redevenir un geste.
 *
 * **Le refus vit ICI, et il ne prend pas de place.** Une première version
 * déployait quand même pour faire apparaître un panneau de refus plein cadre
 * (`CanvasRetenuPanel`) ; à l'usage il « mangeait la page » — la moitié de
 * l'écran occupée par un blocage, dans une conversation où l'avocat voulait
 * simplement discuter. Le refus est donc rendu **sur place** : rien ne se
 * déploie, et l'avocat reçoit la raison en une phrase. Le chemin de retour, lui,
 * existe déjà en permanence dans la barre latérale (`RedactionEnCoursBanner`,
 * #859), qui nomme la conversation en train de rédiger et y ramène.
 *
 * Placer le refus au geste plutôt que sur un bouton lui donne aussi sa portée
 * réelle : les portes humaines (pilule du bandeau, résultat de plan, ouverture
 * d'une pièce depuis un dossier) passent par ici, donc aucune ne peut l'oublier.
 *
 * **Le fil doit être CONFIRMÉ** (#1331). Il n'existe plus de branche « aucune
 * conversation » : un Canvas sans propriétaire enregistrait son brouillon sous
 * `conversationId: null`, que personne ne rouvrait jamais dans son fil, puis
 * comptait sur une adoption tardive au premier message pour se rattraper.
 *
 * Le paramètre reste `string | null` parce que ses appelants lisent une
 * conversation affichée qui peut l'être ; c'est la garde ci-dessous qui tranche,
 * et elle rejoue la preuve AU MOMENT DU GESTE : `aria-disabled` sur le bouton
 * est une annonce, pas un verrou, et plusieurs secondes peuvent séparer le rendu
 * du clic.
 *
 * Rend `null` quand l'ouverture est refusée — l'appelant ne doit alors ni
 * télécharger, ni poser, ni naviguer.
 */
export function ouvrirLeCanvasPourLAvocat(conversationId: string | null): CanvasOpenOutcome | null {
  if (!estUnFilConfirme(conversationId) || conversationId === null) {
    console.warn(
      `[canvas] ouverture refusée pour conversationId=${conversationId ?? 'aucune'} : `
      + "l'existence de ce fil n'est pas prouvée par le serveur (ni confirmation "
      + 'temps réel, ni donnée de conversation). Envoyer un premier message, ou '
      + 'sélectionner un fil existant, puis relancer le geste.',
    );
    return null;
  }
  // ⚠️ **Un fil en cours de SUPPRESSION refuse GRACIEUSEMENT ici** (#1334). Le
  // runtime refuse aussi, mais en LEVANT : c'est le backstop exhaustif des six
  // entrées agent, qui vivent dans un `try` et répondent par une trame d'erreur
  // corrélée. Un geste humain, lui, part d'un gestionnaire de clic sans
  // `try/catch` (`AgentV3View`) : y laisser remonter l'exception ferait surgir
  // la frontière d'erreur au lieu d'un refus. Même doctrine que le fil non
  // confirmé juste au-dessus — le refus vit au geste, pas sur le bouton.
  if (estEnCoursDeSuppression(conversationId)) {
    console.warn(
      `[canvas] ouverture refusée pour conversationId=${conversationId} : `
      + 'la suppression de ce fil est en cours. Rien n\'a été monté. Si la '
      + 'suppression échoue, le fil et son document sont conservés et le geste '
      + 'redevient possible.',
    );
    return null;
  }
  // Le geste réserve seulement l'identité. CanvasResidence observe ensuite
  // cette réservation et passe par requestResident : elle seule peut
  // précharger, checkpoint puis refroidir une victime. Monter ici court-
  // circuiterait la capacité et créerait brièvement un troisième SuperDoc.
  canvasDocumentRuntime.acquireForConversation(conversationId);
  // La résidence sélectionne depuis la scène affichée. Ne pas muter ici le
  // holder/projection avant qu'elle ait gelé le document visible précédent.
  return openCanvas('geste de l\'avocat');
}

/** Le montage s'est terminé sur un échec : inutile d'attendre plus longtemps. */
export class CanvasMountFailedError extends Error {
  constructor(detail: string) {
    super(`le montage du canvas a échoué : ${detail}`);
    this.name = 'CanvasMountFailedError';
  }
}

/**
 * Le document a changé pendant l'attente : ce n'est plus celui qu'on attendait.
 *
 * Rendre l'éditeur du nouveau document serait pire qu'un refus — le lot
 * d'actions viserait un document que son demandeur n'a jamais vu. C'est le
 * scénario du gabarit chargé par l'avocat pendant qu'un lot patiente.
 */
export class CanvasMountSupersededError extends Error {
  constructor(armedEpoch: number, settledEpoch: number) {
    super(
      `le document du canvas a changé pendant l'attente (montage ${armedEpoch} → ${settledEpoch}) : ` +
      'ce n\'est plus le document auquel cette demande était destinée.',
    );
    this.name = 'CanvasMountSupersededError';
  }
}

/** L'attente a expiré : le canvas n'est pas devenu écrivable à temps. */
export class CanvasMountTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(
      `le canvas n'est pas devenu écrivable en ${timeoutMs} ms. Le panneau est monté dans la coque, ` +
      'donc son montage n\'est pas empêché par la page affichée — c\'est sa construction qui n\'a pas abouti.',
    );
    this.name = 'CanvasMountTimeoutError';
  }
}

/** L'attente a été abandonnée volontairement (changement de compte). */
export class CanvasOpenAbandonedError extends Error {
  constructor(reason: string) {
    super(`l'attente du canvas a été abandonnée : ${reason}.`);
    this.name = 'CanvasOpenAbandonedError';
  }
}

interface PendingOpen {
  reject: (error: Error) => void;
  dispose: () => void;
}

const pendingOpens = new Set<PendingOpen>();

/**
 * L'éditeur réellement écrivable, ou `null`.
 *
 * **Le critère est la ressource physique du scope, pas l'état logique du runtime**.
 * La distinction compte : la promotion marque
 * une session `failed` dès que la révision est illisible, et cet état ne se
 * nettoie qu'au montage suivant. Refuser d'écrire sur ce seul motif condamnerait
 * un éditeur parfaitement fonctionnel pour le reste de la session. La garde
 * documentaire reste séparée : inspection et mutations vérifient ensuite leur
 * session routée et leur époque avant d'utiliser cet éditeur.
 */
function usableEditorInstance(conversationId: string): SuperDocInstance | null {
  const instance = canvasDocumentRuntime
    .getScope(conversationId)
    ?.getPhysicalResources().instance ?? null;
  return instance?.activeEditor ? instance : null;
}

/**
 * Attendre que le canvas devienne écrivable.
 *
 * Résout tout de suite si l'éditeur est déjà là ; sinon se dénoue sur la **fin
 * du montage** — sans sondage — ou sur la borne. Trois refus distincts, parce
 * qu'ils n'appellent pas la même suite : montage échoué, document remplacé
 * pendant l'attente, ou délai dépassé.
 *
 * N'ouvre PAS le canvas : appeler `openCanvas` d'abord. Les deux sont séparés
 * parce que la majorité des chemins n'ont que le premier à faire.
 */
export function waitForCanvasEditorReady(
  conversationId: string,
  timeoutMs: number = CANVAS_MOUNT_WAIT_MS,
): Promise<SuperDocInstance> {
  const immediate = usableEditorInstance(conversationId);
  if (immediate) return Promise.resolve(immediate);

  // Le montage exact en cours au moment où l'on s'arme. `null` signifie que
  // l'identité a été réservée avant que React n'ait créé son scope.
  const armedEpoch = canvasDocumentRuntime
    .getSessionForConversation(conversationId)?.mountEpoch ?? null;

  return new Promise<SuperDocInstance>((resolve, reject) => {
    const entry: PendingOpen = { reject, dispose: () => {} };

    const timer = setTimeout(() => {
      entry.dispose();
      reject(new CanvasMountTimeoutError(timeoutMs));
    }, timeoutMs);

    const unsubscribe = canvasDocumentRuntime.onSessionSettled((session) => {
      if (session.conversationId !== conversationId) {
        const target = canvasDocumentRuntime.getSessionForConversation(conversationId);
        // Un autre scope peut aboutir en parallèle sans rien dire de celui-ci.
        // Le remplacement n'est prouvé que pour une époque exacte déjà armée.
        if (armedEpoch === null || target?.mountEpoch === armedEpoch) return;
        entry.dispose();
        reject(new CanvasMountSupersededError(armedEpoch, target?.mountEpoch ?? -1));
        return;
      }
      if (session.state === 'loading') return;
      const instance = usableEditorInstance(session.conversationId);
      if (!instance) {
        // Fin de montage sans éditeur exploitable : rien de plus ne viendra de
        // CE montage. Attendre la borne n'apporterait qu'un message moins précis.
        entry.dispose();
        reject(new CanvasMountFailedError(
          `conversationId=${session.conversationId} mountEpoch=${session.mountEpoch} ` +
          `documentId=${session.documentId ?? 'aucun (canvas généré)'} ` +
          `revision=${session.revision ?? 'illisible'} ; ` +
          'le scope documentaire ne porte aucun éditeur SuperDoc actif.',
        ));
        return;
      }
      if (armedEpoch !== null && session.mountEpoch !== armedEpoch) {
        entry.dispose();
        reject(new CanvasMountSupersededError(armedEpoch, session.mountEpoch));
        return;
      }
      entry.dispose();
      resolve(instance);
    });

    entry.dispose = () => {
      clearTimeout(timer);
      unsubscribe();
      pendingOpens.delete(entry);
    };
    pendingOpens.add(entry);
  });
}

/**
 * Abandonner toutes les attentes en cours.
 *
 * Appelé par l'éviction au changement de compte (`canvasEviction.ts`). Sans
 * cela, une attente ouverte par la requête de l'avocat A se dénouerait sur le
 * **canvas de l'avocat B** — le document du suivant, livré à la demande du
 * précédent. C'est exactement la classe de fuite que la résidence a créée : rien
 * ne se démonte plus, donc rien ne s'annule tout seul.
 */
export function abandonPendingCanvasOpens(reason: string): void {
  for (const entry of [...pendingOpens]) {
    entry.dispose();
    entry.reject(new CanvasOpenAbandonedError(reason));
  }
}

/**
 * Le message rendu à l'agent quand une opération canvas n'a pas pu s'appliquer.
 *
 * Remplace le `'canvas indisponible'` que l'ancien code répétait une fois par
 * action du lot : ni document, ni conversation, ni raison. Un modèle qui lit ce
 * refus hors contexte doit pouvoir dire ce qui a échoué, sur quoi, et quoi faire
 * ensuite — sinon il relance à l'identique.
 *
 * **Le conseil de reprise ne renvoie jamais sur la porte qui vient de refuser.**
 * Conseiller « ré-inspecte » à une inspection refusée produit exactement la
 * boucle inéchappable déjà documentée pour `STALE_CANVAS_REVISION` : le modèle
 * obéit, reçoit le même refus, et recommence jusqu'à épuiser son budget.
 */
export function describeCanvasUnavailable(input: {
  /** Nom de l'opération refusée, tel qu'il apparaît dans les traces. */
  operation: string;
  /** La conversation PORTEUSE du document, pas forcément celle affichée. */
  conversationId: string | null;
  documentTitle: string | null;
  documentId: string | null;
  cause: unknown;
}): string {
  const document = input.documentTitle?.trim()
    ? `le document « ${input.documentTitle.trim()} »`
    : 'le document ouvert (pas encore de titre)';
  const documentRef = input.documentId ? ` [documentId=${input.documentId}]` : '';
  const conversation = input.conversationId
    ? `la conversation ${input.conversationId}`
    : 'une conversation encore inconnue (aucun document n\'a été attribué)';
  const cause = input.cause instanceof Error ? input.cause.message : String(input.cause);

  // Une inspection refusée ne peut pas se réparer par une inspection.
  const reprise = input.operation.includes('inspect')
    ? 'N\'enchaîne PAS sur une nouvelle inspection : elle échouera de la même façon. Dis à l\'avocat '
      + 'que son canvas n\'a pas pu être lu et demande-lui de recharger la page (masquer puis réafficher le canvas ne remonte rien).'
    : 'Ré-inspecte le canvas avant toute nouvelle tentative — et n\'y renvoie pas le même lot à '
      + 'l\'aveugle : s\'il a pu être appliqué en partie, le rejouer dupliquerait le texte.';

  return (
    `${input.operation} n'a pas pu s'appliquer à ${document}${documentRef} de ${conversation} : ${cause} ` +
    'Rien n\'a été écrit dans le document. Le canvas de l\'avocat s\'ouvre sur toutes les pages, y compris ' +
    `quand il consulte un dossier : ce refus ne vient donc PAS d'un canvas fermé. ${reprise}`
  );
}
